import React, { useEffect, useState, useRef, useCallback } from 'react';
import { assetUrl } from '../utils/assets';
import { log } from '../utils/logger';
import './TutorialBubble.css';

interface Position {
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
}

type ReadinessStrategy = 'simple' | 'animation' | 'boundingBoxStability' | 'staggeredList';

interface TutorialBubbleProps {
  skipCompletionMessage?: boolean;
  showBubble?: boolean;
  bubbleMessage?: string;
  bubblePosition?: 'top' | 'bottom' | 'left' | 'right';
  targetElement?: string;
  avatar?: string;
  bubbleButtonType?: 'continue' | 'dismiss' | 'none';
  onContinue?: () => void;
  onDismiss?: () => void;
  readinessStrategy?: ReadinessStrategy;
  delay?: number;
}

const TutorialBubble: React.FC<TutorialBubbleProps> = ({ 
  skipCompletionMessage = false,
  showBubble = false,
  bubbleMessage = '',
  bubblePosition,
  targetElement,
  avatar = 'scientist',
  bubbleButtonType = 'none',
  onContinue,
  onDismiss,
  readinessStrategy = 'simple',
  delay = 0
}) => {
  const [position, setPosition] = useState<Position>({});
  const [targetReady, setTargetReady] = useState(false);
  const [avatarIndex, setAvatarIndex] = useState(0);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const readinessCheckRef = useRef<NodeJS.Timeout>();
  const staggeredStartTimeRef = useRef<number>();
  const lastTargetRectRef = useRef<DOMRect | null>(null);
  
  // Target readiness polling effect
  useEffect(() => {
    if (!showBubble) {
      setTargetReady(false);
      if (readinessCheckRef.current) {
        clearTimeout(readinessCheckRef.current);
      }
      return;
    }

    let checkCount = 0;
    const maxChecks = 10; // 1 second timeout

    const pollReadiness = () => {
      // Check target readiness with current values (no stale closures)
      if (!targetElement) {
        setTargetReady(true);
        return;
      }

      const targetEl = document.querySelector(targetElement);
      if (!targetEl) {
        log.debug(`[Bubble] Target element not found: ${targetElement}`);
        
        checkCount++;
        if (checkCount < maxChecks) {
          readinessCheckRef.current = setTimeout(pollReadiness, 100);
        } else {
          log.debug(`[Bubble] Readiness check timeout, showing bubble anyway`);
          setTargetReady(true);
        }
        return;
      }

      // Check readiness based on strategy
      let isReady = false;
      switch (readinessStrategy) {
        case 'simple':
          // Check element has dimensions AND position is stable (for slide-in animations)
          const simpleRect = targetEl.getBoundingClientRect();
          if (simpleRect.width <= 0 || simpleRect.height <= 0) {
            isReady = false;
          } else if (!lastTargetRectRef.current) {
            // First check - establish baseline position
            lastTargetRectRef.current = simpleRect;
            isReady = false;
          } else {
            // Check if position is stable (within 5px of last position)
            const posDiff = Math.abs(simpleRect.top - lastTargetRectRef.current.top);
            if (posDiff < 5) {
              isReady = true;
            } else {
              lastTargetRectRef.current = simpleRect;
              isReady = false;
            }
          }
          break;
          
        case 'animation':
          // Enhanced animation detection with position stability
          const classList = targetEl.classList;
          const isEntering = classList.contains('card-transition-enter') || 
                            classList.contains('card-transition-enter-active');
          const isExiting = classList.contains('card-transition-exit') || 
                           classList.contains('card-transition-exit-active');
          
          if (isEntering || isExiting) {
            isReady = false;
            log.debug(`[Bubble] Animation still running - isEntering: ${isEntering}, isExiting: ${isExiting}`);
          } else {
            // Transition classes done, now check position stability
            const currentRect = targetEl.getBoundingClientRect();
            if (!lastTargetRectRef.current) {
              log.debug(`[Bubble] Animation done, establishing baseline position:`, { top: currentRect.top, left: currentRect.left });
              lastTargetRectRef.current = currentRect;
              isReady = false; // Need baseline for comparison
            } else {
              const positionDiff = {
                top: Math.abs(currentRect.top - lastTargetRectRef.current.top),
                left: Math.abs(currentRect.left - lastTargetRectRef.current.left)
              };
              const isStable = positionDiff.top < 2 && positionDiff.left < 2;
              log.debug(`[Bubble] Position stability check - diff:`, positionDiff, `stable: ${isStable}`);
              
              if (isStable) {
                isReady = true;
              } else {
                lastTargetRectRef.current = currentRect; // Update baseline
                isReady = false;
              }
            }
          }
          break;
          
        case 'boundingBoxStability':
          const currentRect = targetEl.getBoundingClientRect();
          isReady = currentRect.width > 0 && currentRect.height > 0;
          break;
          
        case 'staggeredList':
          const listContainer = document.querySelector('.suggested-phrases ul');
          if (listContainer) {
            const listItems = listContainer.querySelectorAll('li');
            const targetIndex = targetElement.includes(':first-child') ? 0 : 0;
            if (listItems.length > targetIndex) {
              const estimatedDelay = targetIndex * 50 + 300;
              const now = Date.now();
              if (!staggeredStartTimeRef.current) {
                staggeredStartTimeRef.current = now;
              }
              const elapsed = now - staggeredStartTimeRef.current;
              isReady = elapsed >= estimatedDelay;
            }
          }
          break;
          
        default:
          isReady = true;
      }

      log.debug(`[Bubble] Check ${checkCount + 1}/${maxChecks} - ${readinessStrategy} strategy - ready: ${isReady}`);
      
      if (isReady) {
        setTargetReady(true);
      } else {
        checkCount++;
        if (checkCount < maxChecks) {
          readinessCheckRef.current = setTimeout(pollReadiness, 100);
        } else {
          log.debug(`[Bubble] Readiness check timeout, showing bubble anyway`);
          setTargetReady(true);
        }
      }
    };

    // Reset timing ref for staggered lists
    staggeredStartTimeRef.current = undefined;
    // Reset position baseline for animation strategy
    lastTargetRectRef.current = null;
    
    // Reset and start checking
    setTargetReady(false);
    if (delay > 0) {
      readinessCheckRef.current = setTimeout(pollReadiness, delay);
    } else {
      pollReadiness();
    }

    return () => {
      if (readinessCheckRef.current) {
        clearTimeout(readinessCheckRef.current);
      }
    };
  }, [showBubble, targetElement, readinessStrategy, delay]);
  
  // Recalculate position whenever dependencies change
  const calculatePosition = useCallback(() => {
    if (!showBubble || !bubbleRef.current) return;
    
    log.debug("Calculating bubble position for:", { 
      targetElement,
      bubblePosition,
      bubbleMessage: bubbleMessage.substring(0, 30) + '...'
    });
    
    // Position bubble relative to target element if specified
    if (targetElement) {
      const targetEl = document.querySelector(targetElement);
      
      if (targetEl) {
        const targetRect = targetEl.getBoundingClientRect();
        const bubbleRect = bubbleRef.current.getBoundingClientRect();
        
        // Comprehensive debugging as suggested by GPT-4.1
        log.debug("=== BUBBLE POSITIONING DEBUG ===");
        log.debug("1. Window scroll:", { scrollY: window.scrollY, scrollX: window.scrollX });
        log.debug("2. Target rect:", { 
          left: targetRect.left, 
          top: targetRect.top, 
          width: targetRect.width, 
          height: targetRect.height,
          right: targetRect.right,
          bottom: targetRect.bottom
        });
        log.debug("3. Bubble rect:", { 
          width: bubbleRect.width, 
          height: bubbleRect.height 
        });
        log.debug("4. Target element:", targetEl, "Classes:", targetEl.className);
        log.debug("5. Target computed transform:", window.getComputedStyle(targetEl).transform);
        log.debug("6. Viewport size:", { width: window.innerWidth, height: window.innerHeight });
        
        const pos = bubblePosition || 'bottom';
        let newPosition: Position = {};

        // Calculate position based on specified direction, with smart viewport handling
        const isNarrowScreen = window.innerWidth < 500;

        // For top/bottom on narrow screens, center horizontally on screen instead of target
        const getHorizontalCenter = () => {
          const targetCenter = targetRect.left + (targetRect.width / 2) - (bubbleRect.width / 2);
          if (isNarrowScreen) {
            // Center on screen, clamped to viewport
            return Math.max(10, (window.innerWidth - bubbleRect.width) / 2);
          }
          return targetCenter;
        };

        // Pre-calculate positions to check if they fit in viewport
        const positions = {
          top: {
            top: targetRect.top - bubbleRect.height - 20,
            left: getHorizontalCenter()
          },
          bottom: {
            top: targetRect.bottom + 20,
            left: getHorizontalCenter()
          },
          left: {
            left: targetRect.left - bubbleRect.width - 20,
            top: targetRect.top + (targetRect.height / 2) - (bubbleRect.height / 2)
          },
          right: {
            left: targetRect.right + 20,
            top: targetRect.top + (targetRect.height / 2) - (bubbleRect.height / 2)
          }
        };
        
        // Check if preferred position fits in viewport
        const preferred = positions[pos];
        const fitsVertically = preferred.top >= 10 && preferred.top + bubbleRect.height <= window.innerHeight - 10;
        const fitsHorizontally = preferred.left >= 10 && preferred.left + bubbleRect.width <= window.innerWidth - 10;
        
        if (fitsVertically && fitsHorizontally) {
          // Use preferred position
          newPosition = preferred;
        } else {
          // Try alternative positions
          const alternatives = pos === 'top' ? ['bottom', 'left', 'right'] :
                              pos === 'bottom' ? ['top', 'left', 'right'] :
                              pos === 'left' ? ['right', 'top', 'bottom'] :
                              ['left', 'top', 'bottom'];
          
          let foundFit = false;
          for (const alt of alternatives) {
            const altPos = positions[alt as keyof typeof positions];
            const altFitsV = altPos.top >= 10 && altPos.top + bubbleRect.height <= window.innerHeight - 10;
            const altFitsH = altPos.left >= 10 && altPos.left + bubbleRect.width <= window.innerWidth - 10;
            
            if (altFitsV && altFitsH) {
              newPosition = altPos;
              foundFit = true;
              break;
            }
          }
          
          if (!foundFit) {
            // Fall back to preferred position with constraints
            newPosition = preferred;
            if (newPosition.left !== undefined && newPosition.left < 10) {
              newPosition.left = 10;
            }
            if (newPosition.top !== undefined && newPosition.top < 10) {
              newPosition.top = 10;
            }
            if (newPosition.left !== undefined && newPosition.left + bubbleRect.width > window.innerWidth - 10) {
              newPosition.left = window.innerWidth - bubbleRect.width - 10;
            }
            if (newPosition.top !== undefined && newPosition.top + bubbleRect.height > window.innerHeight - 10) {
              newPosition.top = window.innerHeight - bubbleRect.height - 10;
            }
          }
        }
        
        log.debug("Setting bubble position (new logic):", newPosition);
        setPosition(newPosition);
      } else {
        log.debug("Target element not found:", targetElement);
        // If target not found, try again in a moment
        setTimeout(calculatePosition, 100);
      }
    } else {
      // Center the bubble if no target element
      const bubbleRect = bubbleRef.current.getBoundingClientRect();
      const newPosition: Position = {
        top: window.innerHeight / 2 - bubbleRect.height / 2,
        left: window.innerWidth / 2 - bubbleRect.width / 2
      };
      log.debug("Centering bubble (no target):", newPosition);
      setPosition(newPosition);
    }
  }, [showBubble, targetElement, bubblePosition, bubbleMessage]);
  
  // Calculate position when target becomes ready
  useEffect(() => {
    if (targetReady) {
      calculatePosition();
    }
  }, [targetReady, calculatePosition]);
  
  // Recalculate on window resize
  useEffect(() => {
    if (!showBubble || !targetReady) return;

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showBubble, targetReady, calculatePosition]);

  // Alternate avatar image when bubble message changes
  useEffect(() => {
    if (bubbleMessage) {
      setAvatarIndex(prev => (prev + 1) % 2);
    }
  }, [bubbleMessage]);
  
  // Only show bubble when requested by state machine
  if (!showBubble) {
    return null;
  }
  
  // Hide bubble content until target is ready and positioned
  const shouldShowContent = targetReady && (targetElement ? Object.keys(position).length > 0 : true);
  
  // Debug log for shouldShowContent condition
  log.debug('[Bubble] shouldShowContent check:', {
    targetReady,
    hasTargetElement: !!targetElement,
    positionKeys: Object.keys(position).length,
    shouldShowContent,
    targetElement
  });
  
  const avatarImages = [assetUrl('/assets/tutorial/tutorial_man_1.png'), assetUrl('/assets/tutorial/tutorial_man_2.png')];
  const avatarImage = avatarImages[avatarIndex];
  
  log.debug('[Bubble] Rendering with:', {
    shouldShowContent,
    position,
    targetElement,
    bubbleMessage: bubbleMessage.substring(0, 20) + '...'
  });

  return (
    <div className="tutorial-overlay">
      <div 
        ref={bubbleRef}
        className="tutorial-bubble-container"
        style={{
          position: 'fixed',
          ...(shouldShowContent ? position : { top: -9999, left: -9999 }), // Off-screen when measuring
          opacity: shouldShowContent ? 1 : 0,
          pointerEvents: shouldShowContent ? 'auto' : 'none'
        }}
      >
        {/* Always render content for dimension calculation, but conditionally show it */}
        <div 
          className={`tutorial-bubble tutorial-bubble-${bubblePosition || 'bottom'} ${targetElement ? 'has-target' : ''}`}
          style={{
            visibility: shouldShowContent ? 'visible' : 'hidden'
          }}
        >
          <div className="tutorial-content">
            <img src={avatarImage} alt="Tutorial guide" className="tutorial-avatar" />
            <div className="tutorial-message">
              <p>{bubbleMessage}</p>
              {bubbleButtonType === 'continue' && onContinue && (
                <button 
                  className="tutorial-continue-button" 
                  onClick={() => {
                    log.debug('Continue button clicked');
                    onContinue();
                  }}
                >
                  Continue
                </button>
              )}
              {bubbleButtonType === 'dismiss' && onDismiss && (
                <button className="tutorial-dismiss-btn" onClick={onDismiss}>
                  Got it
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialBubble;